import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import {
  BaseHttpService,
  HttpResult,
} from "../../core/services/base-http.service";
import { BaseResponse } from "../../shared/models/base-response.model";
import {
  CodeExchangeRequestModel,
  CodeExchangeResponseModel,
  VerifyLinkLoginRequestModel,
  VerifyLinkLoginResponseModel,
} from "./auth.model";
import { apiEndpoints } from "../../core/constants/api-strings";

@Injectable({ providedIn: "root" })
export class AuthApiService extends BaseHttpService {
  private readonly authServerBase = environment.kisan_oauth_api_url;
  private clientId = environment.kisan_oauth_client_id;
  private readonly redirectUri = environment.kisan_oauth_ui_url;

  /**
   * Build the full OAuth2 authorization URL to which the user should be redirected.
   *
   * @param codeChallenge  The PKCE code challenge generated by the client.
   * @param state          A unique string to maintain state between request and callback.
   * @returns              A URL like:
   *                       https://your-ui.com/?response_type=code&client_id=…&code_challenge=…&state=…
   */
  buildAuthorizationUrl(codeChallenge: string, state: string,refSource?:string): string {
    const params = [
      "response_type=code",
      "state=" + state,
      "client_id=" + this.clientId,
      "code_challenge=" + codeChallenge,
      ];
      if(refSource){
        params.push("ref_source=" + refSource);
      }
    return `${this.redirectUri}?${params.join("&")}`;
  }

  /**
   * Exchange an authorization code for access (and refresh) tokens.
   *
   * @param authProgressId   The identifier for this auth flow (e.g. from your backend).
   * @param code             The authorization code returned by the OAuth server.
   * @param codeVerifier     The original PKCE code verifier.
   * @returns                An Observable wrapping your standard BaseResponse<TokenModel>.
   */
  exchangeCodeForToken(
    authKey: string,
    code: string,
    codeVerifier: string,
    ref_source?: string
  ): Observable<HttpResult<BaseResponse<CodeExchangeResponseModel>>> {
    let codeExchangeBody: CodeExchangeRequestModel = {
      action: "farmer_signin",
      challenge_name: "code_exchange",
      challenge_data: {
        auth_progress_id: authKey,
        grant_type: "authorization_code",
        code: code,
        code_verifier: codeVerifier,
        ref_source: ref_source,
      },
    };
    const endpoint = apiEndpoints.auth.submitChallenge;
    const url = `${this.authServerBase}${endpoint}`;

    return this.post<BaseResponse<CodeExchangeResponseModel>>(
      url,
      codeExchangeBody,
      {},
      false
    );
  }

  /**
   * Verifies the link login using the provided authentication key and logged ID.
   *
   * @param authKey - The authentication key used to validate the login progress.
   * @param loggedId - The ID of the logged-in user, or null if not applicable.
   * @returns An observable that emits the result of the link login verification,
   *          which includes a base response and the verification response model.
   */
  verifyLinkLogin(
    authKey: string,
    loggedId: string | null,
    isGallabox?: boolean
  ): Observable<HttpResult<BaseResponse<VerifyLinkLoginResponseModel>>> {
    if (isGallabox) {
      this.clientId = environment.gallabox_client_id;
    }
    let codeExchangeBody: VerifyLinkLoginRequestModel = {
      action: "link_login",
      challenge_name: "validate_auth_progress",
      challenge_data: {
        auth_progress_id: authKey,
        client_id: this.clientId,
      },
    };
    if (loggedId) {
      codeExchangeBody.challenge_data.logged_id = loggedId;
    }
    const endpoint = apiEndpoints.auth.verifyLinkLogin;
    const url = `${this.authServerBase}${endpoint}`;

    return this.post<BaseResponse<VerifyLinkLoginResponseModel>>(
      url,
      codeExchangeBody,
      {},
      false
    );
  }
}
